import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faBolt, faGift } from '@fortawesome/free-solid-svg-icons'

import { track } from '../lib/analytics'

const PATH = '/ebook-bikin-toko-online'

/**
 * Compact ebook card sized to sit in the empty right half of a dark page header
 * (e.g. the blog hero). Self-contained link to the ebook landing page.
 */
export function EbookHeaderCard({ location }: { location: string }) {
  return (
    <a
      href={PATH}
      onClick={() =>
        track('click_internal_link', {
          link_url: PATH,
          link_text: 'Promo Ebook Toko Online',
          link_location: location,
        })
      }
      className="group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-amber-400/30 bg-slate-800/50 p-4 shadow-xl backdrop-blur-sm transition-transform hover:-translate-y-0.5 sm:gap-5 sm:p-5"
    >
      <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-amber-500/10 blur-3xl" aria-hidden="true" />
      <img
        src="/ebook/cover-sm.webp"
        alt="Sampul ebook Membangun Toko Online Sendiri dengan WordPress"
        width="600"
        height="849"
        loading="lazy"
        className="relative w-16 flex-shrink-0 rotate-[-3deg] rounded-lg shadow-2xl ring-1 ring-white/10 sm:w-20"
      />
      <div className="relative min-w-0 flex-1">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-amber-300">
          <FontAwesomeIcon icon={faGift} className="h-2.5 w-2.5" />
          Bonus Video Tutorial
        </span>
        <p className="mt-2 text-lg font-black leading-tight tracking-tight text-white sm:text-xl">
          Bangun <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">toko online sendiri</span>
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-base font-black text-white sm:text-lg">Rp 150.000</p>
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2 text-xs font-black text-slate-900 transition-colors group-hover:bg-amber-300">
            Lihat Ebook
            <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </a>
  )
}

/**
 * Compact cross-page promo for the WordPress store-building ebook. Rendered as
 * a full-width band (drop it right before a page's <Footer>). The whole card is
 * one internal link to the ebook landing page; `location` labels the source for
 * the click_internal_link event so we can see which surface drives ebook views.
 */
export function EbookPromo({ location }: { location: string }) {
  return (
    <section className="bg-slate-50 py-8 md:py-10">
      <div className="mx-auto max-w-5xl px-4">
        <a
          href={PATH}
          onClick={() =>
            track('click_internal_link', {
              link_url: PATH,
              link_text: 'Promo Ebook Toko Online',
              link_location: location,
            })
          }
          className="group relative block overflow-hidden rounded-3xl border border-amber-400/25 bg-slate-900 p-6 shadow-xl transition-transform hover:-translate-y-0.5 md:p-8"
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            aria-hidden="true"
            style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
          />
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" aria-hidden="true" />
          <div className="pointer-events-none absolute -left-20 bottom-0 h-56 w-56 rounded-full bg-blue-500/15 blur-3xl" aria-hidden="true" />

          <div className="relative flex flex-col items-center gap-6 md:flex-row md:gap-8">
            <img
              src="/ebook/cover-sm.webp"
              alt="Sampul ebook Membangun Toko Online Sendiri dengan WordPress"
              width="600"
              height="849"
              loading="lazy"
              className="w-24 flex-shrink-0 rotate-[-3deg] rounded-lg shadow-2xl ring-1 ring-white/10 md:w-28"
            />

            <div className="flex-1 text-center md:text-left">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-300">
                <FontAwesomeIcon icon={faGift} className="h-3 w-3" />
                Bonus Video Tutorial
              </span>
              <p className="mt-3 text-2xl font-black leading-tight tracking-tight text-white md:text-3xl">
                Bangun <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">toko online sendiri</span>
              </p>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-slate-300 md:mx-0">
                Panduan PDF untuk menyiapkan toko online dengan WordPress dan WooCommerce. Harga
                ebook sudah termasuk akses video tutorial.
              </p>
            </div>

            <div className="flex flex-shrink-0 flex-col items-center gap-3">
              <div className="text-center">
                <p className="text-2xl font-black leading-none text-white md:text-3xl">Rp 150.000</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-slate-400">Sekali bayar</p>
              </div>
              <span className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-6 py-3 text-sm font-black text-slate-900 transition-colors group-hover:bg-amber-300">
                <FontAwesomeIcon icon={faBolt} className="h-4 w-4" />
                Lihat isi ebook
                <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </div>
        </a>
      </div>
    </section>
  )
}
